import { useState } from 'react';
import { Pencil, Plus, Sparkles, Trash2 } from 'lucide-react';
import { Button, PackIconGlyph, cn } from '@jeraisonne/ui';
import { SCHOOL_LEVELS, SUBJECTS, UPCOMING_SUBJECTS } from '@jeraisonne/db';
import {
  levelLabel,
  packPeriodLabel,
  pickProgramme,
  programmeLabel,
  programmeStatus,
  sortPacks,
  subjectLabel,
  type ProgrammeStatus,
} from '@jeraisonne/db/curriculum';
import { CARD_LEVEL_PALETTES } from '@jeraisonne/db/cards';
import { PackEditor } from '@/features/curriculum/pack-editor';
import { ProgrammeForm } from '@/features/curriculum/programme-form';
import { ProgrammeLivePanel } from '@/features/curriculum/programme-live-panel';
import {
  useAdminPacks,
  useDeletePack,
  useGeneratePackPlan,
  useProgrammes,
  useSetPackStatus,
  type AdminPack,
  type PackStatus,
} from '@/lib/curriculum/api';

const STATUS: Record<PackStatus, { label: string; className: string }> = {
  draft: { label: 'Brouillon', className: 'bg-amber-100 text-amber-800' },
  published: { label: 'Publié', className: 'bg-emerald-100 text-emerald-800' },
  archived: { label: 'Archivé', className: 'bg-ink-100 text-ink-500' },
};

const PROGRAMME: Record<ProgrammeStatus, { label: string; className: string }> = {
  active: { label: 'En vigueur', className: 'bg-emerald-100 text-emerald-800' },
  upcoming: { label: 'À venir', className: 'bg-sky-100 text-sky-800' },
  ended: { label: 'Terminé', className: 'bg-ink-100 text-ink-500' },
};

const selectClass = 'border-ink-200 h-11 rounded-lg border bg-white px-3 text-sm';

/**
 * Route admin `/tutor/admin/packs` (lot F, migrations 0289-0292).
 *
 * Un PROGRAMME officiel (niveau + matière, texte officiel, date de rentrée,
 * date de fin facultative) porte la VALIDITÉ ; ses PACKS portent une période
 * INDICATIVE dans l'année (planning : mois de début → mois de fin) et des
 * SKILLCARDS (many-to-many : une carte peut être dans plusieurs packs). Seuls
 * les packs publiés d'un programme EN VIGUEUR sont visibles du site, des
 * élèves et des profs.
 */
export function TutorAdminPacksPage() {
  const [level, setLevel] = useState<string>('3e');
  // Matières ouvertes + matières en préparation (brouillons, admin seulement).
  const subjects = [
    ...SUBJECTS.map((s) => ({ value: s.value as string, label: s.label, levels: s.levels })),
    ...UPCOMING_SUBJECTS.map((s) => ({
      value: s.value,
      label: `${s.label} (en préparation)`,
      levels: s.levels,
    })),
  ].filter((s) => s.levels.includes(level as never));
  const [subjectChoice, setSubject] = useState<string>('math');
  const subject = subjects.some((s) => s.value === subjectChoice)
    ? subjectChoice
    : (subjects[0]?.value ?? 'math');
  const programmes = useProgrammes(level, subject);
  const [programmeChoice, setProgrammeChoice] = useState<string | null>(null);
  const programmeList = programmes.data ?? [];
  const programme =
    programmeList.find((p) => p.id === programmeChoice) ?? pickProgramme(programmeList) ?? null;
  const [editingProgramme, setEditingProgramme] = useState<'new' | 'edit' | null>(null);

  const packs = useAdminPacks(level, subject);
  const setStatus = useSetPackStatus(level, subject);
  const remove = useDeletePack(level, subject);
  const generate = useGeneratePackPlan(level, subject);
  const [editing, setEditing] = useState<AdminPack | 'new' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [instructions, setInstructions] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const allPacks = packs.data ?? [];
  const list = sortPacks(allPacks.filter((p) => p.programme_id === programme?.id));
  const drafts = list.filter((p) => p.status === 'draft');
  const color = CARD_LEVEL_PALETTES[level as keyof typeof CARD_LEVEL_PALETTES]?.accent ?? '#4DA3FF';

  const resetSelection = () => {
    setEditing(null);
    setEditingProgramme(null);
    setProgrammeChoice(null);
  };

  const onGenerate = async () => {
    setNotice(null);
    try {
      const n = await generate.mutateAsync({ instructions, programmeId: programme?.id });
      setNotice(
        `${n} pack${n > 1 ? 's' : ''} créé${n > 1 ? 's' : ''} en brouillon. Relis-les avant de publier.`,
      );
    } catch (e) {
      setNotice(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-6 md:p-8">
      <div>
        <h1 className="font-display text-ink-900 text-2xl font-extrabold">
          Programmes et packs de la progression
        </h1>
        <p className="text-ink-500 text-sm">
          Le PROGRAMME officiel porte la validité (rentrée, fin facultative) ; ses PACKS suivent le
          planning de l’année (période indicative) et regroupent des SKILLCARDS — une même carte
          peut figurer dans plusieurs packs. Seuls les packs publiés d’un programme en vigueur sont
          visibles du site, des élèves et des profs.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <select
          aria-label="Niveau"
          className={selectClass}
          value={level}
          onChange={(e) => {
            setLevel(e.target.value);
            resetSelection();
          }}
        >
          {SCHOOL_LEVELS.map((l) => (
            <option key={l.value} value={l.value}>
              {l.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Matière"
          className={selectClass}
          value={subject}
          onChange={(e) => {
            setSubject(e.target.value);
            resetSelection();
          }}
        >
          {subjects.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        {programmeList.length > 0 && (
          <select
            aria-label="Programme"
            className={selectClass}
            value={programme?.id ?? ''}
            onChange={(e) => {
              setProgrammeChoice(e.target.value);
              setEditing(null);
              setEditingProgramme(null);
            }}
          >
            {programmeList.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title} · {PROGRAMME[programmeStatus(p.valid_from, p.valid_until)].label}
              </option>
            ))}
          </select>
        )}
        <Button type="button" variant="secondary" onClick={() => setEditingProgramme('new')}>
          <Plus className="h-4 w-4" /> Nouveau programme
        </Button>
      </div>

      {editingProgramme && (
        <ProgrammeForm
          key={editingProgramme === 'new' ? 'new' : programme?.id}
          level={level}
          subject={subject}
          programme={editingProgramme === 'edit' ? programme : null}
          defaultTitle={`${subjectLabel(subject)} ${levelLabel(level)} — programme de la rentrée …`}
          onDone={(id) => {
            setEditingProgramme(null);
            if (id) setProgrammeChoice(id);
          }}
        />
      )}

      {programmes.data && programmeList.length === 0 && !editingProgramme && (
        <p className="text-ink-600 text-sm">
          Aucun programme pour ce niveau et cette matière : crée-le (date de rentrée, texte
          officiel) avant d’y ajouter des packs.
        </p>
      )}

      {programme && !editingProgramme && (
        <section
          className="border-ink-200 space-y-1 rounded-2xl border bg-white p-4"
          aria-label="Programme sélectionné"
        >
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-display text-ink-900 text-base font-bold">{programme.title}</h2>
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-xs font-semibold',
                PROGRAMME[programmeStatus(programme.valid_from, programme.valid_until)].className,
              )}
            >
              {PROGRAMME[programmeStatus(programme.valid_from, programme.valid_until)].label}
            </span>
            <button
              type="button"
              aria-label="Modifier le programme"
              onClick={() => setEditingProgramme('edit')}
              className="text-ink-500 hover:bg-ink-50 ml-auto flex h-11 w-11 items-center justify-center rounded-lg"
            >
              <Pencil className="h-4 w-4" />
            </button>
          </div>
          <p className="text-ink-600 text-sm">
            {programmeLabel(programme.valid_from, programme.valid_until)}
          </p>
          {programme.source && <p className="text-ink-500 text-xs">{programme.source}</p>}
        </section>
      )}

      {programme && !editingProgramme && <ProgrammeLivePanel programmeId={programme.id} />}

      {programme && (
        <>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" onClick={() => setEditing('new')}>
              <Plus className="h-4 w-4" /> Nouveau pack
            </Button>
            {drafts.length > 0 && (
              <Button
                type="button"
                disabled={setStatus.isPending}
                onClick={() =>
                  setStatus.mutate({ ids: drafts.map((d) => d.id), status: 'published' })
                }
              >
                Publier les {drafts.length} brouillons
              </Button>
            )}
          </div>

          <details className="border-ink-200 rounded-2xl border bg-white p-4">
            <summary className="text-ink-900 cursor-pointer text-sm font-semibold">
              <Sparkles className="text-primary-500 mr-1 inline h-4 w-4" /> Générer un brouillon
              avec l’IA
            </summary>
            <div className="mt-3 space-y-2">
              <p className="text-ink-500 text-xs">
                L’IA découpe l’année de CE programme en packs (planning indicatif) à partir de ses
                chapitres et des SKILLCARDS publiées du niveau, et liste les cartes manquantes. Les
                packs sont AJOUTÉS en brouillon (rien n’est remplacé).
              </p>
              <textarea
                className="border-ink-200 w-full rounded-lg border p-2 text-sm"
                rows={2}
                placeholder="Consignes facultatives (ex. commencer par la géométrie)…"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
              />
              <Button type="button" disabled={generate.isPending} onClick={() => void onGenerate()}>
                {generate.isPending ? 'Génération…' : 'Générer le brouillon'}
              </Button>
            </div>
          </details>
        </>
      )}

      {notice && (
        <p role="status" className="bg-primary-50 text-primary-800 rounded-xl px-3 py-2 text-sm">
          {notice}
        </p>
      )}

      {programme && editing === 'new' && (
        <PackEditor
          level={level}
          subject={subject}
          pack={null}
          programmeId={programme.id}
          allPacks={allPacks}
          defaultColor={color}
          onDone={() => setEditing(null)}
        />
      )}

      {packs.isLoading && <div className="bg-ink-100 h-24 animate-pulse rounded-2xl" />}
      {programme && packs.data && list.length === 0 && (
        <p className="text-ink-600 text-sm">Aucun pack dans ce programme.</p>
      )}

      {programme && (
        <ol className="space-y-2" aria-label="Packs du programme">
          {list.map((p) =>
            editing !== 'new' && editing?.id === p.id ? (
              <li key={p.id}>
                <PackEditor
                  level={level}
                  subject={subject}
                  pack={p}
                  programmeId={programme.id}
                  allPacks={allPacks}
                  defaultColor={color}
                  onDone={() => setEditing(null)}
                />
              </li>
            ) : (
              <li
                key={p.id}
                className="border-ink-200 flex flex-wrap items-center gap-3 rounded-2xl border bg-white p-3"
              >
                <span
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                  style={{ background: `${p.color ?? color}22`, color: p.color ?? color }}
                >
                  <PackIconGlyph icon={p.icon} className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-ink-900 font-semibold">
                    <span className="text-ink-500 mr-2 text-xs uppercase">
                      {packPeriodLabel(p.start_month, p.end_month)}
                    </span>
                    {p.title}
                  </p>
                  <p className="text-ink-500 text-xs">
                    {p.cards.length} SKILLCARDS · {p.chapter_ids.length} chapitre
                    {p.chapter_ids.length > 1 ? 's' : ''}
                    {p.missing_cards.length > 0 && ` · ${p.missing_cards.length} à créer`}
                  </p>
                </div>
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-xs font-semibold',
                    STATUS[p.status].className,
                  )}
                >
                  {STATUS[p.status].label}
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={setStatus.isPending}
                  onClick={() =>
                    setStatus.mutate({
                      ids: [p.id],
                      status: p.status === 'published' ? 'draft' : 'published',
                    })
                  }
                >
                  {p.status === 'published' ? 'Dépublier' : 'Publier'}
                </Button>
                <button
                  type="button"
                  aria-label={`Modifier ${p.title}`}
                  onClick={() => setEditing(p)}
                  className="text-ink-500 hover:bg-ink-50 flex h-11 w-11 items-center justify-center rounded-lg"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  aria-label={
                    confirmDelete === p.id
                      ? `Confirmer la suppression de ${p.title}`
                      : `Supprimer ${p.title}`
                  }
                  onClick={() => {
                    if (confirmDelete === p.id) {
                      remove.mutate(p.id);
                      setConfirmDelete(null);
                    } else setConfirmDelete(p.id);
                  }}
                  className={cn(
                    'flex h-11 items-center justify-center gap-1 rounded-lg px-3 text-sm',
                    confirmDelete === p.id
                      ? 'bg-accent-50 text-accent-700 font-semibold'
                      : 'text-ink-400 hover:text-accent-600',
                  )}
                >
                  <Trash2 className="h-4 w-4" />
                  {confirmDelete === p.id && 'Confirmer'}
                </button>
              </li>
            ),
          )}
        </ol>
      )}
    </div>
  );
}
